import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useProvidersStore } from '../stores/providersStore';
import type { Provider } from '../stores/providersStore';
import { AppLayout } from '../components/layout';
import { Card, Button, Input, Textarea } from '../components/ui';

// Validation schema
const providerSchema = z.object({
	name: z.string().min(1, 'Provider name is required'),
	email: z.string().min(1, 'Email is required').email('Invalid email format'),
	phone: z.string().min(1, 'Phone number is required').regex(/^[\d\s\-\(\)\+]+$/, 'Invalid phone number format'),
	specialty: z.string().optional(),
	city: z.string().optional(),
	zipCode: z
		.string()
		.optional()
		.refine(val => !val || /^\d{5}(-\d{4})?$/.test(val), {
			message: 'Invalid zip code format (e.g., 12345 or 12345-6789)',
		}),
	address: z.string().optional(),
	joinDate: z.string().min(1, 'Join date is required'),
	bbpFee: z.number().min(0, 'BBP fee must be at least 0').max(100, 'BBP fee cannot exceed 100'),
	bookingEnabled: z.boolean(),
	notes: z.string().optional(),
});

type ProviderFormData = z.infer<typeof providerSchema>;

// Dummy API calls
const createProvider = async (data: ProviderFormData): Promise<Provider> => {
	await new Promise(resolve => setTimeout(resolve, 1000));

	return {
		id: `provider-${Date.now()}`,
		...data,
		specialty: data.specialty || 'General',
		city: data.city || 'New York',
		appointments: 0,
		revenue: 0,
	};
};

const updateProvider = async (id: string, data: ProviderFormData, existingProvider: Provider): Promise<Provider> => {
	await new Promise(resolve => setTimeout(resolve, 1000));

	return {
		...existingProvider,
		...data,
		id,
	};
};

export default function ProviderForm() {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const providers = useProvidersStore(state => state.providers);
	const addProvider = useProvidersStore(state => state.addProvider);
	const updateProviderInStore = useProvidersStore(state => state.updateProvider);

	// Determine if we're in edit mode
	const isEditMode = !!id;
	const provider = isEditMode ? providers.find(p => p.id === id) : null;

	const {
		register,
		handleSubmit,
		formState: { errors, isSubmitting },
	} = useForm<ProviderFormData>({
		resolver: zodResolver(providerSchema),
		defaultValues: isEditMode && provider
			? {
					name: provider.name,
					email: provider.email,
					phone: provider.phone,
					specialty: provider.specialty,
					city: provider.city,
					zipCode: provider.zipCode || '',
					address: provider.address || '',
					joinDate: provider.joinDate,
					bbpFee: provider.bbpFee,
					bookingEnabled: provider.bookingEnabled,
					notes: provider.notes || '',
			  }
			: {
					joinDate: new Date().toISOString().split('T')[0],
					bbpFee: 15,
					bookingEnabled: true,
			  },
	});

	const onSubmit = async (data: ProviderFormData) => {
		if (isEditMode) {
			// Edit mode
			if (!id || !provider) return;

			const updatePromise = updateProvider(id, data, provider);

			toast.promise(updatePromise, {
				loading: 'Updating provider...',
				success: 'Provider updated successfully!',
				error: 'Failed to update provider',
			});

			try {
				const updatedProvider = await updatePromise;
				updateProviderInStore(updatedProvider);
				navigate(`/providers/${id}`);
			} catch (error) {
				console.error('Error updating provider:', error);
			}
		} else {
			// Add mode
			const createPromise = createProvider(data);

			toast.promise(createPromise, {
				loading: 'Adding provider...',
				success: 'Provider added successfully!',
				error: 'Failed to add provider',
			});

			try {
				const newProvider = await createPromise;
				addProvider(newProvider);
				navigate('/providers');
			} catch (error) {
				console.error('Error adding provider:', error);
			}
		}
	};

	const handleCancel = () => {
		if (isEditMode) {
			navigate(`/providers/${id}`);
		} else {
			navigate('/providers');
		}
	};

	// If in edit mode but provider not found, show error
	if (isEditMode && !provider) {
		return (
			<AppLayout>
				<Card>
					<div className="text-center py-12">
						<h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">Provider Not Found</h2>
						<Button variant="primary" onClick={() => navigate('/providers')}>
							Back to Providers
						</Button>
					</div>
				</Card>
			</AppLayout>
		);
	}

	return (
		<AppLayout showTopbar={false}>
			{/* Back Link */}
			<div
				className="flex items-center gap-2 text-blue-600 dark:text-blue-400 cursor-pointer hover:underline mb-4 pb-4 text-sm font-medium border-b border-gray-200 dark:border-gray-700"
				onClick={handleCancel}
			>
				<svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
					<path
						fillRule="evenodd"
						d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
					/>
				</svg>
				{isEditMode ? 'Back to Provider Detail' : 'Back to Providers'}
			</div>

			<Card className="max-w-4xl">
				<h2 className="text-2xl font-bold mb-6 text-gray-900 dark:text-gray-100">
					{isEditMode ? `Edit Provider Profile - ${provider?.name}` : 'Add New Provider'}
				</h2>

				<form onSubmit={handleSubmit(onSubmit)}>
					<div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
						{/* Provider Name */}
						<Input
							label="Provider Name"
							placeholder="Enter provider name"
							error={errors.name?.message}
							required
							disabled={isSubmitting}
							{...register('name')}
						/>

						{/* Email */}
						<Input
							type="email"
							label="Email"
							placeholder="Enter email"
							error={errors.email?.message}
							required
							disabled={isSubmitting}
							{...register('email')}
						/>

						{/* Phone */}
						<Input
							type="tel"
							label="Phone"
							placeholder="Enter phone number"
							error={errors.phone?.message}
							required
							disabled={isSubmitting}
							{...register('phone')}
						/>

						{/* Specialty */}
						<Input
							label="Specialty"
							placeholder="Enter specialty (default: General)"
							error={errors.specialty?.message}
							disabled={isSubmitting}
							{...register('specialty')}
						/>

						{/* City */}
						<Input
							label="City"
							placeholder="Enter city"
							error={errors.city?.message}
							disabled={isSubmitting}
							{...register('city')}
						/>

						{/* Zip Code */}
						<Input
							label="Zip Code"
							placeholder="Enter zip code"
							error={errors.zipCode?.message}
							disabled={isSubmitting}
							{...register('zipCode')}
						/>

						{/* Address - Full Width */}
						<div className="md:col-span-2">
							<Input
								label="Address"
								placeholder="Enter address"
								error={errors.address?.message}
								disabled={isSubmitting}
								{...register('address')}
							/>
						</div>

						{/* Join Date */}
						<Input
							type="date"
							label="Join Date"
							error={errors.joinDate?.message}
							required
							disabled={isSubmitting}
							{...register('joinDate')}
						/>

						{/* BBP Fee */}
						<Input
							type="number"
							label="BBP Fee %"
							placeholder="Enter BBP fee percentage"
							error={errors.bbpFee?.message}
							min={0}
							max={100}
							step={0.1}
							disabled={isSubmitting}
							{...register('bbpFee', { valueAsNumber: true })}
						/>

						{/* Booking Enabled - Full Width */}
						<div className="md:col-span-2">
							<label className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 block">Booking Enabled</label>
							<label className="toggle-switch">
								<input type="checkbox" {...register('bookingEnabled')} disabled={isSubmitting} />
								<span className="toggle-slider"></span>
							</label>
						</div>

						{/* Notes - Full Width */}
						<div className="md:col-span-2">
							<Textarea
								label="Notes"
								placeholder="Enter any additional notes about the provider"
								error={errors.notes?.message}
								disabled={isSubmitting}
								rows={4}
								{...register('notes')}
							/>
						</div>
					</div>

					{/* Action Buttons */}
					<div className="flex flex-col sm:flex-row gap-3 mt-6">
						<Button type="submit" variant="primary" isLoading={isSubmitting} className="w-full sm:w-auto">
							{isSubmitting ? (isEditMode ? 'Saving Changes...' : 'Adding Provider...') : isEditMode ? 'Save Changes' : 'Add Provider'}
						</Button>
						<Button type="button" variant="secondary" onClick={handleCancel} disabled={isSubmitting} className="w-full sm:w-auto">
							Cancel
						</Button>
					</div>
				</form>
			</Card>

			<div className="mt-6 text-sm text-gray-500 dark:text-gray-400">© Beauty Business Portal</div>
		</AppLayout>
	);
}
